import React, { useState, useEffect, useCallback, useRef } from 'react';
import { tradingHoursService } from '../services/tradingHoursService';
import './SimpleTradingHours.css';

const SimpleTradingHours = ({ onChange }) => {
  // FIXED: Remove global state references and fix syntax error
  const [hours, setHours] = useState(null);
  const [totalDays, setTotalDays] = useState(0);
  const [totalHours, setTotalHours] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  
  const mountId = useRef(Math.random().toString(36).substr(2, 9));
  const saveTimeoutRef = useRef(null);
  
  console.log(`SimpleTradingHours mounted with ID: ${mountId.current}`);

  // Format time function
  const formatTime = useCallback((timeString) => {
    if (!timeString) return '09:00';
    if (timeString === '00:00') return '09:00';
    
    const cleaned = timeString.replace(/::+/g, ':').replace(/:$/, '').trim();
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    
    if (timeRegex.test(cleaned)) {
      const [hour, minute] = cleaned.split(':');
      return `${hour.padStart(2, '0')}:${minute}`;
    }
    
    return '09:00';
  }, []);

  // Calculate totals
  const calculateTotals = useCallback((hoursData) => {
    let workingDays = 0;
    let weeklyHours = 0;

    Object.values(hoursData).forEach(day => {
      if (day && day.open === true && day.start && day.end) {
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
        
        if (timeRegex.test(day.start) && timeRegex.test(day.end)) {
          workingDays++;
          
          const startTime = new Date(`2000-01-01T${day.start}:00`);
          const endTime = new Date(`2000-01-01T${day.end}:00`);
          
          if (!isNaN(startTime.getTime()) && !isNaN(endTime.getTime())) {
            const hoursPerDay = (endTime - startTime) / (1000 * 60 * 60);
            if (hoursPerDay > 0) {
              weeklyHours += hoursPerDay;
            }
          }
        }
      }
    });

    return { workingDays, weeklyHours };
  }, []);

  // FIXED: Load data once on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        console.log(`${mountId.current}: Loading trading hours from API...`);
        setLoading(true);
        
        const data = await tradingHoursService.getTradingHours();
        console.log(`${mountId.current}: API Response:`, data);
        
        if (data && data.trading_hours) {
          const formattedHours = {};
          Object.keys(data.trading_hours).forEach(day => {
            const dayData = data.trading_hours[day];
            formattedHours[day] = {
              open: Boolean(dayData.open),
              start: formatTime(dayData.start),
              end: formatTime(dayData.end)
            };
          });
          
          console.log(`${mountId.current}: Formatted hours:`, formattedHours);
          
          const { workingDays, weeklyHours } = calculateTotals(formattedHours);
          
          console.log(`${mountId.current}: Calculated totals - Days: ${workingDays}, Hours: ${weeklyHours}`);
          
          setHours(formattedHours);
          setTotalDays(workingDays);
          setTotalHours(weeklyHours);
          
          // Notify parent immediately with initial data
          if (onChange) {
            onChange({
              tradingHours: formattedHours,
              totalWorkingDays: workingDays,
              totalWorkingHours: weeklyHours
            });
          }
          
          console.log(`${mountId.current}: Trading hours loaded successfully`);
        } else {
          console.error(`${mountId.current}: No trading hours data in API response`);
          setLoadError('No trading hours data found');
        }
      } catch (error) {
        console.error(`${mountId.current}: Error loading trading hours:`, error);
        setLoadError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []); // Only run once per mount

  // Debounced save
  const saveChanges = useCallback(async (hoursData, workingDays, weeklyHours) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        console.log(`${mountId.current}: Saving trading hours...`, {
          hours: hoursData,
          days: workingDays,
          totalHours: weeklyHours
        });
        
        await tradingHoursService.saveTradingHours({
          trading_hours: hoursData,
          total_working_days: workingDays,
          total_working_hours: weeklyHours
        });
        
        console.log(`${mountId.current}: Trading hours saved successfully`);
      } catch (error) {
        console.error(`${mountId.current}: Error saving trading hours:`, error);
      }
    }, 1000);
  }, []);

  // Handle changes and update everything
  const handleChange = useCallback((newHours) => {
    console.log(`${mountId.current}: Handling change`, newHours);
    
    const { workingDays, weeklyHours } = calculateTotals(newHours);
    
    console.log(`${mountId.current}: New totals - Days: ${workingDays}, Hours: ${weeklyHours}`);
    
    // Update local state
    setHours(newHours);
    setTotalDays(workingDays);
    setTotalHours(weeklyHours);
    
    // Notify parent
    if (onChange) {
      onChange({
        tradingHours: newHours,
        totalWorkingDays: workingDays,
        totalWorkingHours: weeklyHours
      });
    }
    
    // Save to database
    saveChanges(newHours, workingDays, weeklyHours);
  }, [calculateTotals, saveChanges, onChange]);

  // Event handlers
  const handleDayToggle = useCallback((day) => {
    if (loading || !hours) return;
    
    console.log(`${mountId.current}: Toggling day ${day}`);
    
    const newHours = {
      ...hours,
      [day]: {
        ...hours[day],
        open: !hours[day].open
      }
    };
    
    handleChange(newHours);
  }, [hours, loading, handleChange]);

  const handleTimeChange = useCallback((day, field, value) => {
    if (loading || !hours) return;
    
    console.log(`${mountId.current}: Changing time ${day} ${field} to ${value}`);
    
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(value)) return;

    const newHours = {
      ...hours,
      [day]: {
        ...hours[day],
        [field]: value
      }
    };
    
    handleChange(newHours);
  }, [hours, loading, handleChange]);

  const quickSetHours = useCallback((startTime, endTime) => {
    if (loading || !hours) return;
    
    console.log(`${mountId.current}: Quick setting hours ${startTime}-${endTime}`);
    
    const newHours = { ...hours };
    
    Object.keys(newHours).forEach(day => {
      if (newHours[day] && newHours[day].open) {
        newHours[day] = {
          ...newHours[day],
          start: formatTime(startTime),
          end: formatTime(endTime)
        };
      }
    });
    
    handleChange(newHours);
  }, [hours, loading, handleChange, formatTime]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      console.log(`SimpleTradingHours unmounted: ${mountId.current}`);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="compact-trading-hours">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          Loading trading hours... (ID: {mountId.current})
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="compact-trading-hours">
        <div style={{ textAlign: 'center', padding: '20px', color: '#e74c3c' }}>
          Error: {loadError}
          <br />
          <small>Please check your connection and try again</small>
        </div>
      </div>
    );
  }

  if (!hours) {
    return (
      <div className="compact-trading-hours">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          No trading hours data available
        </div>
      </div>
    );
  }

  const days = [
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
    { key: 'sunday', label: 'Sun' }
  ];

  return (
    <div className="compact-trading-hours">
      <div style={{ fontSize: '10px', color: '#999', marginBottom: '10px' }}>
        Component ID: {mountId.current}
      </div>
      
      <div className="quick-set-buttons">
        <button 
          type="button"
          className="quick-set-btn"
          onClick={() => quickSetHours('09:00', '17:00')}
        >
          9-5
        </button>
        <button 
          type="button"
          className="quick-set-btn"
          onClick={() => quickSetHours('10:00', '18:00')}
        >
          10-6
        </button>
        <button 
          type="button"
          className="quick-set-btn"
          onClick={() => quickSetHours('09:00', '20:00')}
        >
          9-8
        </button>
      </div>

      <div className="days-grid">
        {days.map(({ key, label }) => {
          const dayData = hours[key];
          if (!dayData) return null;

          return (
            <div key={key} className={`day-row ${dayData.open ? 'open' : 'closed'}`}>
              <div className="day-header">
                <label className="day-checkbox">
                  <input
                    type="checkbox"
                    checked={dayData.open || false}
                    onChange={() => handleDayToggle(key)}
                  />
                  <span className="day-label">{label}</span>
                </label>
              </div>
              
              {dayData.open && (
                <div className="time-inputs">
                  <input
                    type="time"
                    value={dayData.start || '09:00'}
                    onChange={(e) => handleTimeChange(key, 'start', e.target.value)}
                    className="time-input"
                  />
                  <span className="time-separator">-</span>
                  <input
                    type="time"
                    value={dayData.end || '17:00'}
                    onChange={(e) => handleTimeChange(key, 'end', e.target.value)}
                    className="time-input"
                  />
                </div>
              )}
              
              {!dayData.open && (
                <div className="closed-indicator">
                  <span>Closed</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="hours-summary">
        <div className="summary-item">
          <span className="summary-label">Working Days:</span>
          <span className="summary-value">{totalDays} days/week</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Hours:</span>
          <span className="summary-value">{Number(totalHours).toFixed(1)} hrs/week</span>
        </div>
      </div>

      {totalHours > 0 && (
        <div className="hours-note">
          <small>These hours are used to calculate overhead per minute and owner profit per minute.</small>
        </div>
      )}
    </div>
  );
};

export default SimpleTradingHours;